import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router';
import type { DesignElement, MergeField, TicketDesign } from '@bre-ticketing/ticket-design';
import { PREVIEW_SCENARIOS, parseDesign, scenarioByKey, validateDesign } from '@bre-ticketing/ticket-design';
import type { ApiError } from '../api';
import { designEndpoints } from '../api';
import { Canvas } from '../designs/Canvas';
import { Properties } from '../designs/Properties';
import { MediaPicker } from '../media/MediaPicker';
import { usePermissions } from '../session';
import { NotPermitted } from '../App';
import { Badge, Button, ErrorNote, Panel } from '../ui';

/**
 * The ticket design studio (D-89, PARTS 9–17).
 *
 * A real designer: a canvas at the stock's true size, elements dragged and
 * resized, a properties panel, and a running list of everything that would
 * make a ticket fail at the door. Validation runs here as you work and again
 * on the server before publishing, so the browser can be helpful without being
 * trusted.
 */

const NEW_ELEMENTS: { kind: DesignElement['kind']; label: string; make: () => Partial<DesignElement> }[] = [
  { kind: 'TEXT', label: 'Text', make: () => ({ kind: 'TEXT', text: 'Text', font: 'SANS', size_pt: 12, weight: 'NORMAL', align: 'LEFT', width_mils: 2000, height_mils: 300 }) },
  { kind: 'FIELD', label: 'Merge field', make: () => ({ kind: 'FIELD', field: 'event.title', font: 'SANS', size_pt: 12, weight: 'NORMAL', align: 'LEFT', width_mils: 2400, height_mils: 300 }) },
  { kind: 'QR', label: 'QR code', make: () => ({ kind: 'QR', source: 'CREDENTIAL', error_correction: 'M', quiet_zone_mils: 100, width_mils: 1600, height_mils: 1600 }) },
  { kind: 'BARCODE', label: 'Barcode', make: () => ({ kind: 'BARCODE', symbology: 'CODE128', source: 'SHORT_CODE', width_mils: 2000, height_mils: 400 }) },
  { kind: 'IMAGE', label: 'Image', make: () => ({ kind: 'IMAGE', file_id: '', fit: 'CONTAIN', width_mils: 1600, height_mils: 800 }) },
  { kind: 'SPONSOR', label: 'Sponsor logo', make: () => ({ kind: 'SPONSOR', source: 'EVENT_PRESENTING', variant: 'BLACK', width_mils: 1600, height_mils: 600 }) },
  { kind: 'BOX', label: 'Box', make: () => ({ kind: 'BOX', fill_hex: '#f1f5f9', border_hex: '#cbd5e1', border_width_mils: 10, width_mils: 2400, height_mils: 800 }) },
  { kind: 'LINE', label: 'Line', make: () => ({ kind: 'LINE', thickness_mils: 10, width_mils: 3000, height_mils: 10 }) },
];

export default function TicketDesignsPage() {
  const { id } = useParams();
  const can = usePermissions();
  if (!can('ticket_designs.view')) return <NotPermitted permission="ticket_designs.view" />;
  return id ? <Studio designId={id} /> : <DesignList />;
}

function DesignList() {
  const can = usePermissions();
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [canvas, setCanvas] = useState<'PDF' | 'BOCA'>('PDF');

  const designs = useQuery({ queryKey: ['ticket-designs'], queryFn: () => designEndpoints.list() });
  const create = useMutation({
    mutationFn: () => designEndpoints.create({ name, canvas_kind: canvas, scope_type: 'ORGANIZATION' }),
    onSuccess: (result) => {
      setName('');
      void queryClient.invalidateQueries({ queryKey: ['ticket-designs'] });
      window.location.assign(`/ticket-designs/${result.design.id}`);
    },
    onError: (cause: ApiError) => setError(cause.message),
  });

  return (
    <div className="space-y-4">
      {error ? <ErrorNote>{error}</ErrorNote> : null}
      <h1 className="text-lg font-semibold text-ink-900">Ticket designs</h1>

      {can('ticket_designs.manage') ? (
        <Panel title="New design" description="It starts from a complete house design that already prints correctly; change what you like.">
          <div className="flex flex-wrap items-end gap-3 px-5 py-4">
            <label className="text-sm">
              <span className="text-xs font-semibold text-ink-400 uppercase">Name</span>
              <input value={name} onChange={(e) => setName(e.target.value)} className="mt-1 block w-72 rounded border border-line bg-surface px-2 py-1.5" />
            </label>
            <label className="text-sm">
              <span className="text-xs font-semibold text-ink-400 uppercase">For</span>
              <select value={canvas} onChange={(e) => setCanvas(e.target.value as 'PDF' | 'BOCA')} className="mt-1 block rounded border border-line bg-surface px-2 py-1.5">
                <option value="PDF">A PDF ticket (email and print at home)</option>
                <option value="BOCA">Thermal ticket stock (the window)</option>
              </select>
            </label>
            <Button variant="primary" disabled={!name.trim() || create.isPending} onClick={() => create.mutate()}>
              Create
            </Button>
          </div>
        </Panel>
      ) : null}

      <Panel title={`${designs.data?.designs.length ?? 0} designs`}>
        <ul className="divide-y divide-line">
          {(designs.data?.designs ?? []).map((design) => (
            <li key={design.id} className="flex flex-wrap items-center gap-3 px-5 py-3">
              <a href={`/ticket-designs/${design.id}`} className="text-sm font-medium text-accent underline-offset-2 hover:underline">
                {design.name ?? design.code}
              </a>
              <Badge tone={design.status === 'ACTIVE' ? 'ok' : design.status === 'DRAFT' ? 'accent' : 'neutral'}>{design.status.toLowerCase()}</Badge>
              <span className="text-xs text-ink-400">
                v{design.version} · {design.canvas_kind === 'BOCA' ? 'thermal stock' : 'PDF'} · {design.scope_type.toLowerCase().replaceAll('_', ' ')} · {design.elements} elements
              </span>
            </li>
          ))}
          {designs.data?.designs.length === 0 ? <li className="px-5 py-4 text-sm text-ink-600">No designs yet.</li> : null}
        </ul>
      </Panel>
    </div>
  );
}

function Studio({ designId }: { designId: string }) {
  const can = usePermissions();
  const queryClient = useQueryClient();
  const manage = can('ticket_designs.manage');

  const loaded = useQuery({ queryKey: ['ticket-design', designId], queryFn: () => designEndpoints.get(designId) });
  const fieldList = useQuery({ queryKey: ['design-fields'], queryFn: () => designEndpoints.fields() });

  const [design, setDesign] = useState<TicketDesign | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [zoom, setZoom] = useState(0.9);
  const [grid, setGrid] = useState(50);
  const [showGrid, setShowGrid] = useState(true);
  const [scenario, setScenario] = useState('reserved');
  const [picking, setPicking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (loaded.data?.design.design && !design) setDesign(parseDesign(loaded.data.design.design));
  }, [loaded.data, design]);

  const row = loaded.data?.design ?? null;
  const readOnly = !manage || row?.status !== 'DRAFT';
  const fields: MergeField[] = useMemo(() => (fieldList.data?.fields ?? []).filter((field) => !field.canvases || !row || field.canvases.includes(row.canvas_kind)), [fieldList.data, row]);
  const values = useMemo(() => (scenarioByKey(scenario) ?? PREVIEW_SCENARIOS[0]!).values, [scenario]);
  const validation = useMemo(() => (design ? validateDesign(design) : null), [design]);
  const flagged = useMemo(() => new Set((validation?.issues ?? []).filter((issue) => issue.element_id).map((issue) => issue.element_id!)), [validation]);

  const save = useMutation({
    mutationFn: () => designEndpoints.save(designId, design),
    onSuccess: () => {
      setDirty(false);
      setNote('Saved.');
      void queryClient.invalidateQueries({ queryKey: ['ticket-design', designId] });
    },
    onError: (cause: ApiError) => setError(cause.message),
  });

  const publish = useMutation({
    mutationFn: () => designEndpoints.publish(designId),
    onSuccess: (result) => {
      setNote(`Published. ${result.issues.filter((issue) => issue.severity === 'WARNING').length} warning(s) recorded with it.`);
      void queryClient.invalidateQueries({ queryKey: ['ticket-design', designId] });
    },
    onError: (cause: ApiError) => setError(cause.message),
  });

  const newDraft = useMutation({
    mutationFn: () => designEndpoints.newVersion(designId),
    onSuccess: (result) => window.location.assign(`/ticket-designs/${result.design.id}`),
    onError: (cause: ApiError) => setError(cause.message),
  });

  const update = (elements: DesignElement[]) => {
    if (!design) return;
    setDesign({ ...design, elements });
    setDirty(true);
  };

  const add = (make: () => Partial<DesignElement>) => {
    if (!design) return;
    const element = { id: `el-${Date.now().toString(36)}`, x_mils: 600, y_mils: 600, rotation: 0, ...make() } as DesignElement;
    update([...design.elements, element]);
    setSelectedId(element.id);
  };

  const selected = design?.elements.find((element) => element.id === selectedId) ?? null;

  if (loaded.isPending || !design || !row) return <p className="text-sm text-ink-400">Loading…</p>;

  return (
    <div className="space-y-4">
      {error ? <ErrorNote>{error}</ErrorNote> : null}
      {note ? <p className="rounded border border-line bg-surface px-4 py-2 text-sm text-ink-700">{note}</p> : null}

      <div className="flex flex-wrap items-center gap-3">
        <a href="/ticket-designs" className="text-sm text-accent underline-offset-2 hover:underline">
          ← Designs
        </a>
        <h1 className="text-lg font-semibold text-ink-900">{row.name ?? row.code}</h1>
        <Badge tone={row.status === 'ACTIVE' ? 'ok' : row.status === 'DRAFT' ? 'accent' : 'neutral'}>{row.status.toLowerCase()}</Badge>
        <span className="text-xs text-ink-400">
          v{row.version} · {row.canvas_kind === 'BOCA' ? 'thermal stock' : 'PDF'}
        </span>

        <div className="ml-auto flex flex-wrap items-center gap-2">
          <label className="text-sm text-ink-700">
            Showing{' '}
            <select value={scenario} onChange={(e) => setScenario(e.target.value)} className="rounded border border-line bg-surface px-2 py-1 text-sm">
              {PREVIEW_SCENARIOS.map((option) => (
                <option key={option.key} value={option.key}>
                  {option.name}
                </option>
              ))}
            </select>
          </label>
          <label className="text-sm text-ink-700">
            Zoom{' '}
            <select value={zoom} onChange={(e) => setZoom(Number(e.target.value))} className="rounded border border-line bg-surface px-2 py-1 text-sm">
              {[0.5, 0.75, 0.9, 1, 1.5, 2].map((level) => (
                <option key={level} value={level}>
                  {Math.round(level * 100)}%
                </option>
              ))}
            </select>
          </label>
          <label className="flex items-center gap-1 text-sm text-ink-700">
            <input type="checkbox" checked={showGrid} onChange={(e) => setShowGrid(e.target.checked)} />
            Grid
          </label>
          <select value={grid} onChange={(e) => setGrid(Number(e.target.value))} aria-label="Grid size" className="rounded border border-line bg-surface px-2 py-1 text-sm">
            <option value={25}>1/40 in</option>
            <option value={50}>1/20 in</option>
            <option value={100}>1/10 in</option>
            <option value={0}>Off</option>
          </select>
          <a href={`/api/admin/ticket-designs/${designId}/preview?scenario=${scenario}`} target="_blank" rel="noreferrer" className="hidden" aria-hidden />
          <Button onClick={() => void openPreview(designId, design, scenario, false)}>Preview PDF</Button>
          <Button onClick={() => void openPreview(designId, design, scenario, true)}>Test copy</Button>
          {readOnly ? (
            manage ? (
              <Button variant="primary" disabled={newDraft.isPending} onClick={() => newDraft.mutate()}>
                New version
              </Button>
            ) : null
          ) : (
            <>
              <Button disabled={!dirty || save.isPending} onClick={() => save.mutate()}>
                {save.isPending ? 'Saving…' : dirty ? 'Save' : 'Saved'}
              </Button>
              <Button variant="primary" disabled={publish.isPending || !validation?.publishable} onClick={() => publish.mutate()}>
                Publish
              </Button>
            </>
          )}
        </div>
      </div>

      {readOnly && row.status !== 'DRAFT' ? (
        <p className="rounded border border-line bg-surface px-4 py-2 text-sm text-ink-700">
          This version is published, so it cannot be changed — it is what tickets are being printed from. Make a new version to work on it.
        </p>
      ) : null}

      <div className="grid gap-4 xl:grid-cols-[1fr_320px]">
        <div className="space-y-3">
          {!readOnly ? (
            <div className="flex flex-wrap items-center gap-2 rounded border border-line bg-surface px-3 py-2">
              <span className="text-xs font-semibold text-ink-400 uppercase">Add</span>
              {NEW_ELEMENTS.map((option) => (
                <Button key={option.kind} onClick={() => (option.kind === 'IMAGE' ? setPicking(true) : add(option.make))}>
                  {option.label}
                </Button>
              ))}
            </div>
          ) : null}

          <Canvas
            design={design}
            values={values}
            selectedId={selectedId}
            zoom={zoom}
            grid={grid}
            showGrid={showGrid}
            issueIds={flagged}
            onSelect={setSelectedId}
            onChange={update}
            readOnly={readOnly}
          />

          <Panel title="Before this can be printed" description="Errors stop a design being published; warnings are worth a look.">
            {validation && validation.issues.length === 0 ? (
              <p className="px-5 py-3 text-sm text-ok">Nothing to fix. This design will print.</p>
            ) : (
              <ul className="divide-y divide-line">
                {(validation?.issues ?? []).map((issue, index) => (
                  <li key={`${issue.code}-${index}`} className="flex flex-wrap items-center gap-3 px-5 py-2 text-sm">
                    <Badge tone={issue.severity === 'ERROR' ? 'bad' : 'warn'}>{issue.severity.toLowerCase()}</Badge>
                    <span className="text-ink-900">{issue.message}</span>
                    {issue.scenario ? <span className="text-xs text-ink-400">on “{PREVIEW_SCENARIOS.find((s) => s.key === issue.scenario)?.name ?? issue.scenario}”</span> : null}
                    {issue.element_id ? (
                      <Button className="ml-auto" onClick={() => setSelectedId(issue.element_id!)}>
                        Show me
                      </Button>
                    ) : null}
                  </li>
                ))}
              </ul>
            )}
          </Panel>
        </div>

        <div className="space-y-3">
          <Panel title="Properties">
            <Properties
              element={selected}
              fields={fields}
              readOnly={readOnly}
              onChange={(patch) => update(design.elements.map((element) => (element.id === selectedId ? ({ ...element, ...patch } as DesignElement) : element)))}
              onDelete={() => {
                update(design.elements.filter((element) => element.id !== selectedId));
                setSelectedId(null);
              }}
              onDuplicate={() => {
                if (!selected) return;
                const copy = { ...selected, id: `el-${Date.now().toString(36)}`, x_mils: selected.x_mils + 100, y_mils: selected.y_mils + 100 } as DesignElement;
                update([...design.elements, copy]);
                setSelectedId(copy.id);
              }}
            />
          </Panel>

          <Panel title="Elements">
            <ul className="max-h-72 divide-y divide-line overflow-y-auto">
              {design.elements.map((element) => (
                <li key={element.id}>
                  <button
                    type="button"
                    onClick={() => setSelectedId(element.id)}
                    className={`flex w-full items-center gap-2 px-4 py-2 text-left text-sm ${element.id === selectedId ? 'bg-accent-soft' : 'hover:bg-page'}`}
                  >
                    <span className="text-xs text-ink-400 uppercase">{element.kind}</span>
                    <span className="truncate text-ink-900">{element.name ?? (element.kind === 'FIELD' ? element.field : element.kind === 'TEXT' ? element.text : element.id)}</span>
                    {flagged.has(element.id) ? <Badge tone="bad">check</Badge> : null}
                  </button>
                </li>
              ))}
            </ul>
          </Panel>
        </div>
      </div>

      <MediaPicker
        open={picking}
        onClose={() => setPicking(false)}
        category="TICKET"
        title="Choose an image for the ticket"
        onChoose={(asset) => {
          const element = { id: `el-${Date.now().toString(36)}`, kind: 'IMAGE', file_id: asset.id, fit: 'CONTAIN', x_mils: 600, y_mils: 600, width_mils: 1600, height_mils: 800 } as DesignElement;
          update([...design.elements, element]);
          setSelectedId(element.id);
        }}
      />
    </div>
  );
}

/** Renders the design server-side and opens the PDF, which is what will really print. */
async function openPreview(designId: string, design: TicketDesign, scenario: string, test: boolean): Promise<void> {
  const response = await fetch(`/api/admin/ticket-designs/${designId}/preview`, {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'content-type': 'application/json', 'x-bre-csrf': '1' },
    body: JSON.stringify({ scenario, design, test }),
  });
  if (!response.ok) return;
  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  window.open(url, '_blank', 'noopener');
  window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
}
